import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ChevronRight, Check, Sparkles } from 'lucide-react';
import api from '../services/api';
import ContactButton from '../components/ContactButton';

const TaylorCollection = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState({
    suit: null,
    jacket: null,
    pants: null,
    duffle: null
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const [suitRes, jacketRes, pantsRes, duffleRes] = await Promise.all([
        api.get('/shopify/products/taylor-suit').catch(() => null),
        api.get('/shopify/products/the-taylor-jacket').catch(() => null),
        api.get('/shopify/products/the-taylor-pants-1').catch(() => null),
        api.get('/shopify/products/the-valley-duffle').catch(() => null)
      ]);

      setProducts({
        suit: suitRes?.data?.product || null,
        jacket: jacketRes?.data?.product || null,
        pants: pantsRes?.data?.product || null,
        duffle: duffleRes?.data?.product || null
      });
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPrice = (product) => {
    if (!product) return 0;
    return product.variants?.[0]?.price || product.price || 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-warmSage border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero Section - Full Suit as Value Anchor */}
      <section className="relative min-h-screen flex items-center">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_elite-academy-app/artifacts/foubgc1t_IMG_6073.jpg"
            alt="The Taylor Suit - Tunnel Vision Collection"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/30"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 container mx-auto px-6 py-32">
          <div className="max-w-2xl">
            {/* Tunnel Vision Badge */}
            <div className="inline-flex items-center gap-2 bg-warmSage/20 border border-warmSage/40 px-4 py-2 rounded-full mb-8">
              <Sparkles className="w-4 h-4 text-warmSage" />
              <span className="text-warmSage text-sm font-bold tracking-wider uppercase">Tunnel Vision Collection</span>
            </div>

            <h1 className="text-5xl md:text-7xl font-black uppercase tracking-tight mb-6">
              THE TAYLOR
              <span className="block text-warmSage">SUIT</span>
            </h1>

            <p className="text-xl md:text-2xl text-gray-300 mb-4 font-light leading-relaxed">
              Pre-game focus meets post-game presence.
            </p>

            <p className="text-lg text-gray-400 mb-8 leading-relaxed max-w-xl">
              <span className="text-white font-semibold">Tunnel Vision</span> — that locked-in state where distractions fade and only the mission remains. 
              The Taylor Suit was designed for those moments: when you need to command a room with the same confidence you bring to the field.
            </p>

            {/* Key Differentiators */}
            <div className="flex flex-wrap gap-4 mb-10">
              {['Comfort Stretch', 'Movement Freedom', 'All-Day Wear'].map((feature) => (
                <div key={feature} className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-full">
                  <Check className="w-4 h-4 text-warmSage" />
                  <span className="text-sm text-gray-300">{feature}</span>
                </div>
              ))}
            </div>

            {/* Price & CTA */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
              <div>
                <p className="text-gray-500 text-sm uppercase tracking-wider mb-1">Full Suit</p>
                <p className="text-4xl font-black text-white">${getPrice(products.suit)}</p>
              </div>
              <Button 
                onClick={() => navigate('/products/taylor-suit')}
                className="bg-warmSage hover:bg-warmSage/90 text-black font-black text-lg px-8 py-6 rounded-none"
              >
                SHOP THE FULL SUIT
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Brand Philosophy Section */}
      <section className="bg-gradient-to-b from-black to-gray-950 py-24">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-black uppercase mb-6">
              This Is Not a Special-Occasion-Only Suit
            </h2>
            <p className="text-xl text-gray-400 leading-relaxed mb-8">
              We engineered the Taylor with performance fabric that stretches and moves with you — 
              not against you. The result? <span className="text-white font-semibold">Pieces you won't want to take off.</span>
            </p>
            <p className="text-lg text-warmSage font-semibold">
              Designed for comfort, confidence, and compliments.
            </p>
          </div>
        </div>
      </section>

      {/* Jacket Standalone Section */}
      <section className="bg-gray-950 py-24">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <div className="relative group">
              <div className="aspect-[4/5] overflow-hidden">
                <img 
                  src={products.jacket?.images?.[0]?.url || "https://customer-assets.emergentagent.com/job_fashion-empire-2/artifacts/aeoxnjzq_IMG_2796.JPG"}
                  alt="The Taylor Jacket"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              {/* Accent Line */}
              <div className="absolute bottom-0 left-0 w-1/3 h-1 bg-warmSage"></div>
            </div>

            {/* Content */}
            <div className="lg:pl-12">
              <p className="text-warmSage text-sm font-bold tracking-[0.3em] uppercase mb-4">Build Your Look</p>
              <h2 className="text-4xl md:text-5xl font-black uppercase mb-6">
                THE TAYLOR JACKET
              </h2>
              <p className="text-gray-400 text-lg leading-relaxed mb-6">
                The jacket that transitions from tunnel walk to dinner reservation without missing a beat. 
                Structured enough to command respect, flexible enough to forget you're wearing it.
              </p>

              {/* Use Cases */}
              <div className="space-y-3 mb-8">
                <p className="text-sm text-gray-500 uppercase tracking-wider font-bold">Perfect For:</p>
                <div className="flex flex-wrap gap-3">
                  {['Interviews', 'Date Night', 'Game Day Arrivals', 'Business Casual'].map((use) => (
                    <span key={use} className="bg-white/5 border border-gray-800 px-4 py-2 text-sm text-gray-300">
                      {use}
                    </span>
                  ))}
                </div>
              </div>

              {/* Price & CTA */}
              <div className="flex items-center gap-6">
                <p className="text-3xl font-black text-white">${getPrice(products.jacket)}</p>
                <Button 
                  onClick={() => navigate('/products/the-taylor-jacket')}
                  variant="outline"
                  className="border-warmSage text-warmSage hover:bg-warmSage hover:text-black font-bold px-6 py-3 rounded-none"
                >
                  VIEW JACKET
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pants Standalone Section */}
      <section className="bg-black py-24">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Content - Left on Desktop */}
            <div className="lg:pr-12 order-2 lg:order-1">
              <p className="text-warmSage text-sm font-bold tracking-[0.3em] uppercase mb-4">Everyday Versatility</p>
              <h2 className="text-4xl md:text-5xl font-black uppercase mb-6">
                THE TAYLOR PANTS
              </h2>
              <p className="text-gray-400 text-lg leading-relaxed mb-6">
                Tailored, but not tight. Sharp, but never stiff. These pants redefine what it means to dress up — 
                with hidden stretch that lets you move like an athlete, even when you're dressed like an executive.
              </p>

              {/* Features */}
              <div className="space-y-3 mb-8">
                <p className="text-sm text-gray-500 uppercase tracking-wider font-bold">Key Features:</p>
                <ul className="space-y-2">
                  {[
                    'Performance stretch fabric',
                    'Tailored slim fit',
                    'Hidden flex waistband',
                    'Premium hardware'
                  ].map((feature) => (
                    <li key={feature} className="flex items-center gap-3 text-gray-300">
                      <Check className="w-4 h-4 text-warmSage flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Price & CTA */}
              <div className="flex items-center gap-6">
                <p className="text-3xl font-black text-white">${getPrice(products.pants)}</p>
                <Button 
                  onClick={() => navigate('/products/the-taylor-pants-1')}
                  variant="outline"
                  className="border-warmSage text-warmSage hover:bg-warmSage hover:text-black font-bold px-6 py-3 rounded-none"
                >
                  VIEW PANTS
                </Button>
              </div>
            </div>

            {/* Image - Right on Desktop */}
            <div className="relative group order-1 lg:order-2">
              <div className="aspect-[4/5] overflow-hidden">
                <img 
                  src={products.pants?.images?.[0]?.url || "https://customer-assets.emergentagent.com/job_fashion-empire-2/artifacts/ygs0x6wd_IMG_2794.JPG"}
                  alt="The Taylor Pants"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              {/* Accent Line */}
              <div className="absolute bottom-0 right-0 w-1/3 h-1 bg-warmSage"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Valley Duffle Section */}
      <section className="bg-gray-950 py-24">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <div className="relative group">
              <div className="aspect-[4/5] overflow-hidden">
                <img 
                  src={products.duffle?.images?.[0]?.url || "https://cdn.shopify.com/s/files/1/0733/6116/2292/files/IMG_6378.jpg"}
                  alt="The Valley Duffle"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              {/* Accent Line */}
              <div className="absolute bottom-0 left-0 w-1/3 h-1 bg-warmSage"></div>
            </div>

            {/* Content */}
            <div className="lg:pl-12">
              <p className="text-warmSage text-sm font-bold tracking-[0.3em] uppercase mb-4">Complete The Vision</p>
              <h2 className="text-4xl md:text-5xl font-black uppercase mb-6">
                THE VALLEY DUFFLE
              </h2>
              <p className="text-gray-400 text-lg leading-relaxed mb-6">
                Every arrival deserves a statement piece. The Valley Duffle is hand-crafted for those who carry 
                their world with them — a sophisticated vessel that matches the intention of the Taylor Suit. 
                Premium materials meet purposeful design.
              </p>

              {/* Features */}
              <div className="space-y-3 mb-8">
                <p className="text-sm text-gray-500 uppercase tracking-wider font-bold">Signature Details:</p>
                <ul className="space-y-2">
                  {[
                    'Full-grain premium leather',
                    'Gold 3D logo plate',
                    'Pyramid bag feet',
                    'Spacious interior compartments'
                  ].map((feature) => (
                    <li key={feature} className="flex items-center gap-3 text-gray-300">
                      <Check className="w-4 h-4 text-warmSage flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Use Cases */}
              <div className="space-y-3 mb-8">
                <p className="text-sm text-gray-500 uppercase tracking-wider font-bold">Perfect For:</p>
                <div className="flex flex-wrap gap-3">
                  {['Travel Days', 'Game Day Arrivals', 'Business Trips', 'Weekend Getaways'].map((use) => (
                    <span key={use} className="bg-white/5 border border-gray-800 px-4 py-2 text-sm text-gray-300">
                      {use}
                    </span>
                  ))}
                </div>
              </div>

              {/* Price & CTA */}
              <div className="flex items-center gap-6">
                <p className="text-3xl font-black text-white">${getPrice(products.duffle)}</p>
                <Button 
                  onClick={() => navigate('/products/the-valley-duffle')}
                  variant="outline"
                  className="border-warmSage text-warmSage hover:bg-warmSage hover:text-black font-bold px-6 py-3 rounded-none"
                >
                  VIEW DUFFLE
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Complete the Look - Full Suit Close */}
      <section className="relative py-32 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-900"></div>
        <div className="absolute inset-0 opacity-20">
          <div 
            className="w-full h-full"
            style={{
              backgroundImage: 'linear-gradient(rgba(168,162,158,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(168,162,158,0.1) 1px, transparent 1px)',
              backgroundSize: '60px 60px'
            }}
          ></div>
        </div>

        <div className="relative z-10 container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-warmSage/10 border border-warmSage/30 px-6 py-3 rounded-full mb-8">
              <span className="text-warmSage text-sm font-bold tracking-wider uppercase">The Complete Expression</span>
            </div>

            <h2 className="text-4xl md:text-6xl font-black uppercase mb-6">
              BUILD YOUR LOOK NOW
              <span className="block text-warmSage mt-2">COMPLETE THE SUIT WHEN YOU'RE READY</span>
            </h2>

            <p className="text-xl text-gray-400 mb-12 max-w-2xl mx-auto leading-relaxed">
              Each piece was intentionally designed to stand alone — but when worn together, 
              the Taylor Suit becomes a statement of discipline, style, and unshakable presence.
            </p>

            {/* Price Breakdown */}
            <div className="bg-black/50 border border-gray-800 rounded-lg p-8 mb-12 max-w-lg mx-auto">
              <div className="space-y-4">
                <div className="flex justify-between items-center text-lg">
                  <span className="text-gray-400">The Taylor Jacket</span>
                  <span className="text-white font-bold">${getPrice(products.jacket)}</span>
                </div>
                <div className="flex justify-between items-center text-lg">
                  <span className="text-gray-400">The Taylor Pants</span>
                  <span className="text-white font-bold">${getPrice(products.pants)}</span>
                </div>
                <div className="flex justify-between items-center text-lg">
                  <span className="text-gray-400">The Valley Duffle</span>
                  <span className="text-white font-bold">${getPrice(products.duffle)}</span>
                </div>
                <div className="border-t border-gray-700 pt-4 mt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-white font-bold text-xl">Full Taylor Suit</span>
                    <span className="text-warmSage font-black text-3xl">${getPrice(products.suit)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Final CTAs */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                onClick={() => navigate('/products/taylor-suit')}
                className="bg-warmSage hover:bg-warmSage/90 text-black font-black text-lg px-10 py-6 rounded-none w-full sm:w-auto"
              >
                GET THE FULL SUIT
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                onClick={() => navigate('/products/the-taylor-jacket')}
                variant="outline"
                className="border-gray-600 text-gray-300 hover:bg-gray-800 font-bold px-8 py-6 rounded-none w-full sm:w-auto"
              >
                START WITH THE JACKET
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Tunnel Vision Closing */}
      <section className="bg-black py-16 border-t border-gray-900">
        <div className="container mx-auto px-6 text-center">
          <p className="text-warmSage font-bold tracking-[0.3em] uppercase text-sm mb-4">
            Tunnel Vision
          </p>
          <p className="text-gray-500 text-lg max-w-xl mx-auto">
            Stay locked in. The mission is the moment — and the moment is yours.
          </p>
        </div>
      </section>

      <ContactButton />
    </div>
  );
};

export default TaylorCollection;
